import { jsx as _jsx } from "react/jsx-runtime";
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { useCometMcpServers } from '#pplx/frontend/apps/ask/comet-browser/hooks/useCometMcpServers.js';
import { isCoreSourceType } from '#pplx/frontend/apps/ask/sources/coreSourcesConfig.js';
import { useApiSource } from '#pplx/frontend/apps/ask/sources/hooks/useApiSource.js';
import { useSourceIcon } from '#pplx/frontend/apps/ask/sources/hooks/useSourceIcon.js';
import { getFallbackSourceLabel, useSourceLabel, } from '#pplx/frontend/apps/ask/sources/hooks/useSourceLabel.js';
import { isAvailableSourceType } from '#pplx/frontend/apps/ask/sources/types/availableSources.js';
import { getSourceDescription as getAvailableSourceDescription } from '#pplx/frontend/apps/ask/sources/utilities/getSourceDescription.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
export const useSourceMeta = ({ sourceId }) => {
    const intl = useIntl();
    const { cometMcpSources } = useCometMcpServers();
    const getAvailableSourceLabel = useSourceLabel({ cometMcpSources });
    const getAvailableSourceIcon = useSourceIcon({ cometMcpSources });
    const apiSource = useApiSource({
        sourceId: sourceId ?? '',
        reason: 'use-source-meta',
        enabled: !!sourceId,
    });
    const label = useMemo(() => {
        if (!sourceId) {
            return '';
        }
        if (apiSource.source && apiSource.label) {
            return apiSource.label;
        }
        if (isAvailableSourceType(sourceId)) {
            return getAvailableSourceLabel(sourceId);
        }
        return getFallbackSourceLabel(sourceId);
    }, [apiSource.label, apiSource.source, sourceId, getAvailableSourceLabel]);
    const description = useMemo(() => {
        if (!sourceId) {
            return undefined;
        }
        if (apiSource.description) {
            return apiSource.description;
        }
        if (isCoreSourceType(sourceId)) {
            return getAvailableSourceDescription(sourceId, intl);
        }
        return undefined;
    }, [apiSource.description, sourceId, intl]);
    const getIcon = useCallback((size = 'default') => {
        if (!sourceId) {
            return undefined;
        }
        const apiIcon = apiSource.getIcon(size);
        if (apiIcon)
            return apiIcon;
        if (isAvailableSourceType(sourceId)) {
            const availableSourceIcon = getAvailableSourceIcon(sourceId, size);
            if (availableSourceIcon?.icon) {
                if (availableSourceIcon.type === 'sprite') {
                    return _jsx(Icon, { icon: availableSourceIcon.icon, size: size });
                }
                return availableSourceIcon.icon;
            }
        }
        return _jsx(Icon, { icon: $icon('plug'), size: size });
    }, [apiSource, sourceId, getAvailableSourceIcon]);
    const isConnected = useMemo(() => {
        if (apiSource.source) {
            return apiSource.isConnected;
        }
        return true;
    }, [apiSource.source, apiSource.isConnected]);
    const isLoading = useMemo(() => {
        return apiSource.isLoading;
    }, [apiSource.isLoading]);
    const isFetching = useMemo(() => {
        return apiSource.isFetching;
    }, [apiSource.isFetching]);
    return useMemo(() => ({
        label,
        description,
        getIcon,
        isConnected,
        isLoading,
        isFetching,
    }), [label, description, getIcon, isConnected, isLoading, isFetching]);
};
//# sourceMappingURL=useSourceMeta.js.map